import {
  buildResolvedOrgRolePermissions,
  resolveRolePermissions,
  type OrgRolePermissions,
} from "../../permission-catalog";
import type { OrgRoleName } from "../../auth/permissions";
import type { EnabledOrgRoles } from "../../role-catalog";
import { ORG_MEMBERSHIP_ROLES } from "../../role-catalog";
import { Checkbox } from "../ui/checkbox";
import { countEnabledPermissions } from "./role-permission-utils";

export interface OrgRoleListPanelProps {
  enabledOrgRoles: EnabledOrgRoles;
  rolePermissions: OrgRolePermissions;
  selectedRoleKey: OrgRoleName | null;
  onSelectRoleKey: (roleKey: OrgRoleName) => void;
  manageAvailability?: boolean;
  onEnabledRolesChange?: (next: EnabledOrgRoles) => void;
}

export function OrgRoleListPanel({
  enabledOrgRoles,
  rolePermissions,
  selectedRoleKey,
  onSelectRoleKey,
  manageAvailability = false,
  onEnabledRolesChange,
}: OrgRoleListPanelProps) {
  const roles = manageAvailability
    ? ORG_MEMBERSHIP_ROLES
    : ORG_MEMBERSHIP_ROLES.filter((role) => enabledOrgRoles[role.roleKey]);
  const enabledCount = ORG_MEMBERSHIP_ROLES.filter((role) => enabledOrgRoles[role.roleKey]).length;
  const resolved = buildResolvedOrgRolePermissions(rolePermissions, enabledOrgRoles);

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <h2 className="text-sm font-semibold text-foreground">All roles</h2>
        <span className="rounded-md bg-[var(--tint-purple-bg)] px-2 py-0.5 text-xs text-[var(--tint-purple-fg)] dark:bg-violet-500/15 dark:text-violet-300">
          {manageAvailability ? enabledCount : roles.length}
        </span>
      </div>

      <div className="space-y-2">
        {roles.map((role) => {
          const isSelected = selectedRoleKey === role.roleKey;
          const isOwner = role.roleKey === "owner";
          const isEnabled = enabledOrgRoles[role.roleKey];
          const permissionCount = countEnabledPermissions(
            resolved[role.roleKey] ?? resolveRolePermissions(role.roleKey, rolePermissions)
          );

          return (
            <div
              key={role.roleKey}
              className={`flex items-start gap-2 rounded-lg border p-3 transition-all ${
                isSelected
                  ? "border-[var(--primary-soft-border)] border-l-2 border-l-[var(--nav-active-border)] bg-[var(--nav-active-bg)] dark:border-violet-400/40 dark:border-l-violet-400 dark:bg-violet-500/10"
                  : "border-border bg-card hover:bg-muted"
              } ${manageAvailability && !isEnabled ? "opacity-70" : ""}`}
            >
              <button
                type="button"
                onClick={() => onSelectRoleKey(role.roleKey)}
                className="min-w-0 flex-1 text-left"
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-foreground">{role.label}</p>
                    <p className="text-xs text-muted-foreground">{role.roleKey}</p>
                  </div>
                  {isOwner ? (
                    <span className="shrink-0 rounded-md bg-[var(--tint-purple-bg)] px-2 py-0.5 text-xs text-[var(--tint-purple-fg)] dark:bg-violet-500/15 dark:text-violet-300">
                      System
                    </span>
                  ) : null}
                </div>
                <p className="mt-2 line-clamp-2 text-xs leading-relaxed text-muted-foreground">
                  {role.summary}
                </p>
                <p className="mt-2 text-xs text-[var(--tint-purple-fg)] dark:text-violet-300">
                  {permissionCount} permission{permissionCount === 1 ? "" : "s"}
                </p>
              </button>
              {manageAvailability && onEnabledRolesChange ? (
                <Checkbox
                  className="mt-0.5"
                  checked={isEnabled}
                  disabled={isOwner}
                  onCheckedChange={(value: boolean | "indeterminate") => {
                    if (isOwner) return;
                    onEnabledRolesChange({
                      ...enabledOrgRoles,
                      [role.roleKey]: value === true,
                    });
                  }}
                />
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
}
